import { useEffect, useState, useSyncExternalStore } from "react";

export const newPylonMessage = (message: string) => {
  window.Pylon?.("showNewMessage", message, { isHtml: true });
};

export const openChat = () => {
  window.Pylon?.("show");
};

export const closeChat = () => {
  window.Pylon?.("hide");
};

const CHAT_WINDOW_SELECTOR = ".PylonChat-chatWindowFrameContainer";
const CHAT_WINDOW_CLOSED_CLASS = "PylonChat-chatWindowFrameContainer--closed";

// Tracks whether the Pylon chat window is open by observing the `--closed`
// modifier on its container. Read-only DOM observation — do NOT use Pylon's
// onShow/onHide callbacks, which suppress the widget when queued before load.
export const usePylonChatOpen = () => {
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    let classObserver: MutationObserver | undefined;

    const observe = (node: Element) => {
      const update = () =>
        setIsOpen(!node.classList.contains(CHAT_WINDOW_CLOSED_CLASS));
      classObserver = new MutationObserver(update);
      classObserver.observe(node, {
        attributes: true,
        attributeFilter: ["class"],
      });
      update();
    };

    const existing = document.querySelector(CHAT_WINDOW_SELECTOR);
    if (existing) {
      observe(existing);
      return () => classObserver?.disconnect();
    }

    // The widget mounts its container asynchronously after the script loads.
    const interval = setInterval(() => {
      const node = document.querySelector(CHAT_WINDOW_SELECTOR);
      if (node) {
        clearInterval(interval);
        observe(node);
      }
    }, 500);

    return () => {
      clearInterval(interval);
      classObserver?.disconnect();
    };
  }, []);

  return isOpen;
};

let unreadCount = 0;
let unreadSubscribed = false;
let unreadPolling = false;
const unreadListeners = new Set<() => void>();

// Pylon's onChangeUnreadMessagesCount callback has no unsubscribe, so we
// register it once at the module level and fan out to React consumers via a
// store. Subscribe only after the widget container exists, mirroring
// usePylonChatOpen — queuing callbacks before the script loads is unreliable.
const ensureUnreadSubscription = () => {
  // `unreadSubscribed` flips only once the callback is actually attached, so a
  // slow first widget load doesn't permanently disable retry. `unreadPolling`
  // prevents concurrent mounts from starting a second poll (which would stack
  // duplicate, un-removable callbacks).
  if (unreadSubscribed || unreadPolling) return;

  const register = () => {
    unreadSubscribed = true;
    window.Pylon?.("onChangeUnreadMessagesCount", (count: unknown) => {
      unreadCount =
        typeof count === "number" && Number.isFinite(count) ? count : 0;
      unreadListeners.forEach((notify) => notify());
    });
  };

  if (document.querySelector(CHAT_WINDOW_SELECTOR)) {
    register();
    return;
  }

  unreadPolling = true;
  let attempts = 0;
  const interval = setInterval(() => {
    attempts += 1;
    if (document.querySelector(CHAT_WINDOW_SELECTOR)) {
      clearInterval(interval);
      unreadPolling = false;
      register();
    } else if (attempts >= 60) {
      // Widget never appeared (e.g. Pylon disabled); stop polling but allow a
      // later mount to retry in case it loads afterwards.
      clearInterval(interval);
      unreadPolling = false;
    }
  }, 500);
};

export const usePylonUnreadCount = (): number =>
  useSyncExternalStore(
    (onStoreChange) => {
      ensureUnreadSubscription();
      unreadListeners.add(onStoreChange);
      return () => unreadListeners.delete(onStoreChange);
    },
    () => unreadCount,
    () => 0,
  );
